import React, { useState } from "react";
import { Link } from "react-router-dom";
import MapView from "../components/MapView";
import StatisticsChart from "../components/StatisticsChart";
import useFirestore from "../hooks/useFirestore";
import useAuth from "../hooks/useAuth";
import { getStatusColor, getStatusText } from "../utils";
import { Artwork } from "../types/artwork";
import "./Dashboard.css";

const Dashboard: React.FC = () => {
  const { user, loading: authLoading } = useAuth();
  const { artworks, loading: dataLoading, error } = useFirestore();
  const [statusFilter, setStatusFilter] = useState<string>("all");

  if (authLoading) {
    return (
      <div className="dashboard-auth-loading">
        <div className="dashboard-auth-loading-spinner"></div>
        <p className="dashboard-auth-loading-text">Loading authentication...</p>
      </div>
    );
  }

  if (!user) {
    return (
      <div className="dashboard-unauthenticated">
        <div className="dashboard-unauthenticated-content">
          <div className="dashboard-unauthenticated-icon">🎨</div>
          <h1 className="dashboard-unauthenticated-title">
            D'Asaro Artwork Tracker
          </h1>
          <p className="dashboard-unauthenticated-description">
            Please log in to access your artwork collection dashboard
          </p>
          <div className="dashboard-unauthenticated-actions">
            <Link
              to="/login"
              className="dashboard-unauthenticated-button login"
            >
              🔑 Login
            </Link>
            <Link
              to="/register"
              className="dashboard-unauthenticated-button register"
            >
              ✨ Register
            </Link>
          </div>
        </div>
      </div>
    );
  }

  // Statistiche per il dashboard
  const stats = {
    pending: artworks.filter((a) => a.status === "pending").length,
    inProgress: artworks.filter((a) => a.status === "in-progress").length,
    digitized: artworks.filter((a) => a.isDigitized === true).length,
    obtained: artworks.filter((a) => a.status === "obtained").length,
  };

  // Filtra le opere per stato
  const filteredArtworks =
    statusFilter === "all"
      ? artworks
      : statusFilter === "digitized"
      ? artworks.filter((artwork) => artwork.isDigitized === true)
      : artworks.filter((artwork) => artwork.status === statusFilter);

  const total = artworks.length;

  // Ultime opere aggiunte (max 5) filtrate
  const recentArtworks = filteredArtworks
    .sort(
      (a, b) =>
        new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
    )
    .slice(0, 5);

  return (
    <div className="dashboard-container">
      {/* Welcome Header */}
      <div className="dashboard-welcome-header">
        {/* Decorative elements */}
        <div className="dashboard-welcome-decoration-1"></div>
        <div className="dashboard-welcome-decoration-2"></div>

        <div className="dashboard-welcome-content">
          <div className="dashboard-welcome-left">
            <div className="dashboard-welcome-user-section">
              <span className="dashboard-welcome-user-icon">👋</span>
              <div className="dashboard-welcome-user-info">
                <h1 className="dashboard-welcome-title">Welcome back!</h1>
                <p className="dashboard-welcome-username">
                  {user.email?.split("@")[0] || "User"}
                </p>
              </div>
            </div>
            <div className="dashboard-welcome-stats">
              <p className="dashboard-welcome-stats-text">
                📊 You have <strong>{total}</strong> artworks in your
                collection
                {stats.pending > 0 && (
                  <span>
                    {" "}
                    • <strong>{stats.pending}</strong> pending
                  </span>
                )}
                {stats.inProgress > 0 && (
                  <span>
                    {" "}
                    • <strong>{stats.inProgress}</strong> in progress
                  </span>
                )}
              </p>
            </div>
          </div>

          <div className="dashboard-welcome-right">
            <Link to="/add-artwork" className="dashboard-add-artwork-button">
              <span className="dashboard-add-artwork-icon">➕</span>
              Add New Artwork
            </Link>
          </div>
        </div>
      </div>

      {/* Statistics Chart */}
      <StatisticsChart stats={stats} onFilterChange={setStatusFilter} />

      {/* Filter Section */}
      <div className="dashboard-filter-section">
        <h3 className="dashboard-filter-title">🔍 Filter Dashboard</h3>

        <div className="dashboard-filter-controls">
          <div className="dashboard-filter-group">
            <label className="dashboard-filter-label">Status:</label>
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="dashboard-filter-select"
            >
              <option value="all">📊 All Artworks ({total})</option>
              <option value="pending">📋 Pending ({stats.pending})</option>
              <option value="in-progress">
                ⚡ In Progress ({stats.inProgress})
              </option>
              <option value="digitized">
                ✅ Digitized ({stats.digitized})
              </option>
              <option value="obtained">🎯 Obtained ({stats.obtained})</option>
            </select>
          </div>

          {statusFilter !== "all" && (
            <button
              onClick={() => setStatusFilter("all")}
              className="dashboard-clear-filter-button"
            >
              🧹 Clear Filter
            </button>
          )}
        </div>

        {statusFilter !== "all" && (
          <div className="dashboard-filter-status-info">
            <div className="dashboard-filter-status-content">
              <span
                className="dashboard-filter-status-badge"
                style={{ backgroundColor: getStatusColor(statusFilter) }}
              >
                {getStatusText(statusFilter)}
              </span>
              <span className="dashboard-filter-status-text">
                Showing {filteredArtworks.length} of {total} artworks
              </span>
            </div>
          </div>
        )}
      </div>

      {/* Recent Artworks */}
      <div className="dashboard-recent-section">
        <div className="dashboard-recent-header">
          <div className="dashboard-recent-header-left">
            <h2 className="dashboard-recent-title">
              <span className="dashboard-recent-title-icon">📚</span>
              {statusFilter === "all"
                ? "Recent Artworks"
                : `Recent ${getStatusText(statusFilter)} Artworks`}
            </h2>
            <p className="dashboard-recent-subtitle">
              Showing {Math.min(5, filteredArtworks.length)} of{" "}
              {filteredArtworks.length} artworks
            </p>
          </div>
          <Link to="/artworks" className="dashboard-view-all-button">
            👁️ View All Artworks
          </Link>
        </div>

        {dataLoading && (
          <div className="dashboard-artworks-loading">
            <div className="dashboard-artworks-loading-spinner"></div>
            <p className="dashboard-artworks-loading-text">
              Loading artworks...
            </p>
          </div>
        )}

        {error && (
          <div className="dashboard-error">
            <div className="dashboard-error-icon">⚠️</div>
            <h3 className="dashboard-error-title">Error Loading Artworks</h3>
            <p className="dashboard-error-text">{error}</p>
          </div>
        )}

        {!dataLoading &&
          !error &&
          filteredArtworks.length === 0 &&
          statusFilter === "all" && (
            <div className="dashboard-empty-state no-artworks">
              <div className="dashboard-empty-icon large">🎨</div>
              <h3 className="dashboard-empty-title large">
                No artworks in your collection yet
              </h3>
              <p className="dashboard-empty-description">
                Start building your Sicily artwork collection by adding your
                first piece!
              </p>
              <Link
                to="/add-artwork"
                className="dashboard-empty-button add-artwork"
              >
                ➕ Add Your First Artwork
              </Link>
            </div>
          )}

        {!dataLoading &&
          !error &&
          filteredArtworks.length === 0 &&
          statusFilter !== "all" && (
            <div className="dashboard-empty-state no-filtered">
              <div className="dashboard-empty-icon medium">🔍</div>
              <h3 className="dashboard-empty-title">No artworks found</h3>
              <p className="dashboard-empty-description">
                No artworks found with status:{" "}
                <span
                  className="dashboard-empty-filter-badge"
                  style={{ backgroundColor: getStatusColor(statusFilter) }}
                >
                  {getStatusText(statusFilter)}
                </span>
              </p>
              <button
                onClick={() => setStatusFilter("all")}
                className="dashboard-empty-button view-all"
              >
                📊 View All Artworks
              </button>
            </div>
          )}

        {!dataLoading && filteredArtworks.length > 0 && (
          <div className="dashboard-artworks-grid">
            {recentArtworks.map((artwork) => (
              <div key={artwork.id} className="dashboard-artwork-card">
                {/* Decorative corner */}
                <div
                  className="dashboard-artwork-card-decoration"
                  style={{
                    background: `linear-gradient(135deg, ${getStatusColor(
                      artwork.status
                    )}, ${getStatusColor(artwork.status)}aa)`,
                  }}
                ></div>

                <div className="dashboard-artwork-card-content">
                  <span className="dashboard-artwork-card-icon">🎨</span>
                  <div className="dashboard-artwork-card-info">
                    <h4 className="dashboard-artwork-card-title">
                      {artwork.title}
                    </h4>
                    <div className="dashboard-artwork-card-details">
                      <p className="dashboard-artwork-card-detail">
                        <span>👨‍🎨</span> {artwork.artist}
                      </p>
                      <p className="dashboard-artwork-card-detail">
                        <span>📍</span> {artwork.location}
                      </p>
                      <p className="dashboard-artwork-card-detail">
                        <span>🏛️</span> {artwork.institution}
                      </p>
                    </div>
                  </div>
                </div>

                <div className="dashboard-artwork-card-actions">
                  <span
                    className="dashboard-artwork-status-badge"
                    style={{ backgroundColor: getStatusColor(artwork.status) }}
                  >
                    <span>
                      {artwork.status === "pending" && "📋"}
                      {artwork.status === "in-progress" && "⚡"}
                      {artwork.status === "obtained" && "🎯"}
                      {artwork.isDigitized && "✅"}
                    </span>
                    {getStatusText(artwork.status)}
                  </span>
                  <Link
                    to={`/artwork/${artwork.id}`}
                    className="dashboard-artwork-view-button"
                  >
                    👁️ View
                  </Link>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Map View */}
      <div className="dashboard-map-section">
        <div className="dashboard-map-header">
          <div className="dashboard-map-header-left">
            <h2 className="dashboard-map-title">
              <span className="dashboard-map-title-icon">🗺️</span>
              Artworks Map
            </h2>
            <p className="dashboard-map-subtitle">
              {statusFilter !== "all"
                ? `Showing ${filteredArtworks.length} ${getStatusText(
                    statusFilter
                  ).toLowerCase()} artworks on the map`
                : `Explore all ${filteredArtworks.length} artworks across Sicily`}
            </p>
          </div>

          {statusFilter !== "all" && (
            <div className="dashboard-map-filter-indicator">
              <span
                className="dashboard-map-filter-badge"
                style={{ backgroundColor: getStatusColor(statusFilter) }}
              >
                {getStatusText(statusFilter)}
              </span>
              <span className="dashboard-map-filter-text">Filter Active</span>
            </div>
          )}
        </div>

        <div className="dashboard-map-container">
          <MapView artworks={filteredArtworks} />
        </div>
      </div>
    </div>
  );
};

export default Dashboard;
